import { InfoCircleOutlined } from '@ant-design/icons';
import { getCountryIsoCode } from '@mahawi/eshop-common/src/get-country-iso-code';
import {
  type ICurrencyType,
  type ILeadSession,
  type ILeadSessionHeaders,
  type ILeadSessionScreen,
} from '@mahawi/eshop-common/src/types';
import { type RootState } from '@reducers';
import { Descriptions, Popover, Skeleton, Space, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import duration, { Duration } from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { EMPTY_VALUE } from '../../fragments/empty-value';
import { getCountryString } from '../../fragments/get-country-string';
import { SPACING } from '../../theme/tokens';
import ProductTable from '../product/table';
import SessionTimeline from './session-timeline';
const { Title } = Typography;

dayjs.extend(relativeTime);
dayjs.extend(duration);

const currencyType: ICurrencyType = {
  isoCode: 'EUR',
  name: 'Euro',
  symbol: '€',
  isSymbolBeforePrice: false,
  decimalPrecision: 2,
};

function MessengerDetail(): React.ReactElement {
  const messengerSessions = useSelector((s: RootState) => s.Messenger.sessions);
  const messengerLeadUUID = useSelector(
    (s: RootState) => s.Messenger.lead.uuid,
  );
  const messengerBasketProducts = useSelector(
    (s: RootState) => s.Messenger.basketProducts,
  );
  const languageType = useSelector((s: RootState) => s.Language.languageType);
  const configUrl = useSelector((s: RootState) => s.Config.url);

  const sessions = useMemo((): React.ReactElement | null => {
    if (!messengerSessions) {
      return null;
    }

    return (
      <div className="sessions-list">
        {messengerSessions.map((session: ILeadSession): React.ReactElement => {
          const headers: ILeadSessionHeaders | undefined | null =
            session?.headers;
          const sessionStart: Dayjs = dayjs(session.createdAt);
          const screen: ILeadSessionScreen | undefined | null = session?.screen;

          let durationString: string = 'none';

          if (session.tick) {
            const sessionEnd: Dayjs = dayjs(session.tick);
            const sessionDuration: Duration = dayjs.duration(
              sessionEnd.diff(sessionStart),
            );
            if (sessionDuration.asDays() >= 1) {
              durationString = sessionDuration.format('D [days] HH:mm:ss');
            } else {
              durationString = sessionDuration.format(EDateFormat.TIME);
            }
          }

          return (
            <div key={session.uuid}>
              <Descriptions
                title={session.uuid.slice(0, 8)}
                bordered
                size="small"
                column={3}
                items={[
                  {
                    key: 'tick',
                    label: 'Tick',
                    children: (
                      <Space size={SPACING.lg} align="start">
                        {dayjs(session.tick || session.createdAt).fromNow()}
                        <Popover
                          trigger="hover"
                          content={
                            <Typography.Text>
                              {dayjs(session.tick ?? session.createdAt).format(
                                EDateFormat.DATE_TIME,
                              )}
                            </Typography.Text>
                          }
                        >
                          <InfoCircleOutlined />
                        </Popover>
                      </Space>
                    ),
                  },
                  {
                    key: 'duration',
                    label: 'Duration',
                    children: (
                      <Space size={SPACING.lg} align="start">
                        {durationString}
                        <Popover
                          trigger="hover"
                          content={
                            <Typography.Text>
                              created at{' '}
                              {dayjs(session.createdAt).format(
                                EDateFormat.DATE_TIME,
                              )}
                            </Typography.Text>
                          }
                        >
                          <InfoCircleOutlined />
                        </Popover>
                      </Space>
                    ),
                  },
                  {
                    key: 'country',
                    label: 'Country',
                    children:
                      headers &&
                      headers['cf-connecting-ip'] &&
                      headers['cf-ipcountry'] !== 'undefined' ? (
                        <>
                          {getCountryString(headers['cf-ipcountry'])}
                          <br />
                          <small>{headers['cf-connecting-ip']}</small>
                        </>
                      ) : (
                        EMPTY_VALUE
                      ),
                  },
                  {
                    key: 'ua',
                    label: 'UA',
                    span: 3,
                    children: (
                      <Typography.Text type="secondary">
                        {session?.ua?.ua}
                      </Typography.Text>
                    ),
                  },
                  {
                    key: 'screen',
                    label: 'Screen',
                    span: 1,
                    children: screen ? (
                      <Space orientation="vertical">
                        <Typography.Text>{`${screen.width}x${screen.height} (${screen.availWidth}x${screen.availHeight})`}</Typography.Text>

                        <Typography.Text>
                          colorDepth: {screen.colorDepth}
                        </Typography.Text>

                        <Typography.Text>
                          pixelDepth: {screen.pixelDepth}
                        </Typography.Text>
                      </Space>
                    ) : (
                      EMPTY_VALUE
                    ),
                  },
                  {
                    key: 'query',
                    label: 'Query',
                    span: 2,
                    children: (
                      <Typography.Text type="secondary">
                        {session?.query && Object.keys(session.query).length > 0
                          ? Object.keys(session.query).map(
                              (key: string): React.ReactElement => (
                                <div key={key}>
                                  {key}:&nbsp;
                                  {session.query && session.query[key]
                                    ? String(session.query[key])
                                    : EMPTY_VALUE}
                                </div>
                              ),
                            )
                          : EMPTY_VALUE}
                      </Typography.Text>
                    ),
                  },
                  {
                    key: 'path',
                    label: 'Path',
                    span: 2,
                    children: (
                      <Typography.Text type="secondary">
                        {session?.path || EMPTY_VALUE}
                      </Typography.Text>
                    ),
                  },
                  {
                    key: 'referer',
                    label: 'Referer',
                    span: 1,
                    style: { maxWidth: '10%' },
                    children: (
                      <Typography.Text type="secondary">
                        {headers?.referer || EMPTY_VALUE}
                      </Typography.Text>
                    ),
                  },
                  {
                    key: 'timeline',
                    label: 'Timeline',
                    span: 3,
                    children: (
                      <SessionTimeline
                        session={session}
                        languageType={languageType}
                        configUrl={configUrl}
                      />
                    ),
                  },
                ]}
              />
            </div>
          );
        })}
      </div>
    );
  }, [messengerSessions, languageType?.code, configUrl]);

  const lastSessionCountry: string | null = useMemo((): string | null => {
    if (!messengerSessions || messengerSessions.length === 0) {
      return null;
    }

    const lastSession: ILeadSession | undefined | null = [
      ...messengerSessions,
    ].sort(
      (a: ILeadSession, b: ILeadSession): number =>
        dayjs(b.createdAt).unix() - dayjs(a.createdAt).unix(),
    )[0];

    if (!lastSession || !lastSession.headers) {
      return null;
    }

    const headers: ILeadSessionHeaders = lastSession.headers;

    const countryTypeIsoCode2: string | null =
      headers &&
      headers['cf-connecting-ip'] &&
      headers['cf-ipcountry'] !== 'undefined'
        ? headers['cf-ipcountry']
        : null;

    if (!countryTypeIsoCode2) {
      return null;
    }

    return getCountryIsoCode({ isoCode2: countryTypeIsoCode2 });
  }, [messengerSessions]);

  return (
    <>
      <Title level={5}>Basket</Title>

      {!messengerLeadUUID && !messengerBasketProducts && (
        <Typography.Paragraph>---</Typography.Paragraph>
      )}

      {messengerLeadUUID && (
        <ProductTable
          products={messengerBasketProducts}
          currencyType={currencyType}
          taxCountryTypeIsoCode={lastSessionCountry || 'CZE'}
        />
      )}

      <Title level={5}>Sessions</Title>

      {!messengerLeadUUID && !messengerSessions && (
        <Typography.Paragraph>---</Typography.Paragraph>
      )}

      {messengerLeadUUID && !messengerSessions && <Skeleton active />}

      {messengerSessions?.length === 0 && (
        <Typography.Paragraph>Empty</Typography.Paragraph>
      )}

      {sessions}
    </>
  );
}

export default MessengerDetail;
